"use client";

import { useEffect } from "react";
import {
  appHeightForViewport,
  hasViewportResetMarker,
  isLetterboxed,
  isSoftwareKeyboardEditor,
  withoutViewportResetMarker,
  withViewportResetMarker,
} from "@/lib/standalone-viewport";

/** How long a correction burst keeps polling after it starts. */
const BURST_WINDOW_MS = 10_000;
/** Poll cadence during a burst. */
const POLL_MS = 250;
/** Give iOS one short settle window before replacing the document. */
const HARD_RESET_AT_MS = 700;

/**
 * Keeps the `--app-h` CSS variable equal to the real viewport height in px.
 * Fit-mode screens size themselves with `var(--app-h, 100dvh)` instead of
 * bare `100dvh`.
 *
 * Why dvh alone is not enough: on iOS launch AND relaunch (standalone PWA)
 * WebKit can lay the page out against a stale, shorter viewport and `100dvh`
 * resolves short — the shell column ends ~50pt above the screen bottom.
 * WebKit later corrects the viewport *silently* (no resize / visualViewport
 * event, no dvh re-resolution), so nothing re-lays out until a client
 * navigation. The `apple-mobile-web-app-capable` meta (d97ca67) fixes the
 * first launch after install, but field testing shows subsequent cold
 * relaunches and resumes can still open short — so this layer stays as
 * permanent defense, restarting its correction poll on every resume signal
 * (`pageshow`, `visibilitychange`), not just on mount.
 *
 * Measurement: the larger viewport catches whichever source reports an iOS
 * stale-window correction first. While an editor is focused at scale 1, the
 * smaller visual viewport is used instead so the keyboard cannot cover chat.
 * Pinch zoom keeps the larger layout viewport. Renders nothing.
 *
 * Reset: measuring cannot help when the WINDOW itself opens short (JS sees
 * the same stale number). Next.js client navigation, History API changes,
 * and viewport-meta mutations also leave the native window untouched. When
 * the launch remains letterboxed, replace the current document once with an
 * internally-marked URL. The marker crosses the hard navigation, prevents a
 * reload loop if iOS stays broken, and is removed from the visible URL after
 * the replacement document mounts.
 */
export function AppHeightSync() {
  useEffect(() => {
    const html = document.documentElement;
    let last = 0;
    let poll: ReturnType<typeof setInterval> | null = null;
    let stopPoll: ReturnType<typeof setTimeout> | null = null;
    let hardResetTimer: ReturnType<typeof setTimeout> | null = null;

    // A query marker is more reliable than sessionStorage here: it survives
    // the replacement navigation but does not permanently suppress a reset
    // on a later cold launch after iOS restores the cleaned canonical URL.
    let hardResetAttempted = hasViewportResetMarker(window.location.href);
    if (hardResetAttempted) {
      window.history.replaceState(
        window.history.state,
        "",
        withoutViewportResetMarker(window.location.href),
      );
    }

    const sync = () => {
      const vv = window.visualViewport;
      const h = appHeightForViewport({
        innerHeight: window.innerHeight,
        visualHeight: vv?.height ?? 0,
        visualScale: vv?.scale ?? 1,
        editing: isSoftwareKeyboardEditor(document.activeElement),
      });
      if (h > 0 && h !== last) {
        last = h;
        html.style.setProperty("--app-h", `${h}px`);
      }
      const offsetTop = Math.round(vv?.offsetTop ?? 0);
      html.style.setProperty("--app-offset-top", `${offsetTop}px`);
    };

    const hardReset = () => {
      if (hardResetAttempted || !isLetterboxed()) return;
      hardResetAttempted = true;
      window.location.replace(withViewportResetMarker(window.location.href));
    };

    const stopBurst = () => {
      if (poll) clearInterval(poll);
      if (stopPoll) clearTimeout(stopPoll);
      if (hardResetTimer) clearTimeout(hardResetTimer);
      poll = null;
      stopPoll = null;
      hardResetTimer = null;
    };

    // The silent correction fires no event, so poll for a window after every
    // launch/resume signal instead of only once at mount.
    const startBurst = () => {
      stopBurst();
      sync();
      poll = setInterval(sync, POLL_MS);
      stopPoll = setTimeout(stopBurst, BURST_WINDOW_MS);
      if (!hardResetAttempted && isLetterboxed()) {
        hardResetTimer = setTimeout(hardReset, HARD_RESET_AT_MS);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") startBurst();
    };

    startBurst();
    window.addEventListener("pageshow", startBurst);
    document.addEventListener("visibilitychange", onVisible);
    // Real size changes (rotation, window resize) fire reliably outside the
    // launch bug; track them for the lifetime of the app.
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", sync);

    return () => {
      stopBurst();
      window.removeEventListener("pageshow", startBurst);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", sync);
    };
  }, []);

  return null;
}
